import { createContext, ReactNode, useContext, useEffect, useState } from "react";

import { api, setAccessToken } from "@/lib/api";

export type User = {
  id: number;
  email: string;
  full_name: string;
  role: "admin" | "ketoan" | "kho";
  active: boolean;
};

type AuthCtx = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try silent refresh on initial mount
    api
      .post<{ access_token: string }>("/auth/refresh")
      .then(async (r) => {
        setAccessToken(r.data.access_token);
        const me = await api.get<User>("/auth/me");
        setUser(me.data);
      })
      .catch(() => {
        setAccessToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  async function login(email: string, password: string) {
    const r = await api.post<{ access_token: string; user: User }>("/auth/login", {
      email,
      password,
    });
    setAccessToken(r.data.access_token);
    setUser(r.data.user);
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  }

  return <Ctx.Provider value={{ user, loading, login, logout }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth outside provider");
  return ctx;
}
